import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Upload, Space, Progress, Tooltip, Alert, Typography } from 'antd'
import { apm } from '@elastic/apm-rum'
import { Icon } from '@iconify/react'
import { FileText } from 'react-feather'
import clsx from 'clsx'
import * as tus from 'tus-js-client'
import { produce } from 'immer'
import {
  RcCustomRequestOptions,
  RcFile as AntRcFile,
  UploadFile as AntUploadFile
} from 'antd/lib/upload/interface'

import getUploadUrl from '@pepper/utils/src/api/files/getUploadURL'
import getFileExtension from '@pepper/utils/src/function/getFileExtension'
import noop from '@pepper/utils/src/function/noop'
import { fetchFile } from '@pepper/utils/src/api/files/files'

import { Button, Text } from '../index'
import { useShowMessage } from '../../providers'

import styles from './_.module.css'

export interface UploadFile extends AntUploadFile {
  id?: string | number | undefined
  flag?: boolean | undefined
  base64?: { file: string; name: string; type: string } | undefined
}

interface RcFile extends AntRcFile {
  flag?: boolean | undefined
}

interface DefaultFileItem {
  name: string
  id: string
  type: string
  uid?: string
}

interface Base64 {
  file: string
  name: string
  type: string
}

// File upload component with base64 file conversion
export interface FileUploadProps {
  // css className on the container
  className?: string
  // enable multiple file uploading
  multipleUpload?: boolean
  // enable multiple file selection when uploading
  multipleSelection?: boolean
  // info text for upload
  info?: React.ReactElement | string
  // text on the button
  caption?: string
  // setting default files on component mount, send <file> object array containing {name, id, type}
  defaultFileList?: DefaultFileItem[]
  // enable base64 upload, if enabled, base64 property containing {file: base64string, name, type} will also be sent in handleFiles
  base64?: boolean
  // maximum size of a single file in MBs
  maxFileSize?: number
  // extension that should be accepted for uploading
  allowedFiles?: string[]
  // text at end of the input
  suffix?: string
  //To disable the upload button
  disabled?: boolean
  // handle form function to set form state sents <file> Object containing {name, id, uid, type} as parameter or array of <file> Object when multiple props is true
  handleFiles: (files: UploadFile[] | UploadFile) => void
  // type of ui
  type?: 'default' | 'minimal'
  // limit the files number
  limit?: number | null
  // To display image preview
  showImagePreview?: boolean
  // To display list
  showList?: boolean
  onChange?: (file: UploadFile, fileList: UploadFile[]) => void
  loadV2?: boolean
  loadEditorView?: boolean
  assignmentId?: string
  isAuthorized?: boolean
  //to show v2 ui
  category?: ProjectCategory
  editorType?: string
  setFileLoaderLoading?: (val: boolean) => void
}

// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['bytes', 'kb', 'mb', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i]
}

const truncateString = (str: string, num: number) => {
  if (str.length < num) return str

  const truncatedString = `${str.slice(0, num > 3 ? num - 3 : num)}..`
  const extension = getFileExtension(str)

  return `${truncatedString}.${extension}`
}

let objectURL: string | null = null
const viewableFileExtensions = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'pdf',
  'svg',
  'mp3',
  'mp4',
  'avi',
  'flv',
  'weba',
  'webm',
  'mpeg'
]

export const FileUpload = ({
  className = '',
  caption = 'Browse from your computer',
  info,
  multipleUpload = false,
  multipleSelection = false,
  defaultFileList = [],
  maxFileSize = 10,
  allowedFiles = ['.docx', '.pdf', '.png', '.jpeg', 'jpg'],
  suffix,
  disabled = false,
  handleFiles,
  onChange = noop,
  type = 'default',
  limit = null,
  showImagePreview = false,
  showList = true,
  loadV2,
  loadEditorView,
  assignmentId,
  isAuthorized,
  category,
  editorType,
  setFileLoaderLoading
}: FileUploadProps): React.ReactElement => {
  const showMessage = useShowMessage()
  const _defaultFileListAdded = useRef(false)
  const [files, setFiles] = useState<UploadFile[]>([])
  const [currFileBase64] = useState<Base64 | undefined>(undefined)
  const [upload, setUpload] = useState<Record<string, tus.Upload | null>>({})

  // update filelist from parent component on component mount and add uid necessary for antd Upload component
  useEffect(() => {
    if (files.length > 0) _defaultFileListAdded.current = true
    if (
      !_defaultFileListAdded.current &&
      defaultFileList &&
      defaultFileList.length > 0 &&
      files.length === 0
    ) {
      _defaultFileListAdded.current = true
      const tempFiles: any = []
      defaultFileList.forEach((fileListItem: any) => {
        if (fileListItem.id)
          tempFiles.push({ ...fileListItem, uid: fileListItem.id })
      })
      setFiles(tempFiles)
    }
  }, [files, defaultFileList])

  // send files to parent component when updated
  useEffect(() => {
    // send files that have an id
    const uploadedFiles = [...files].filter(file => file.id !== undefined)
    if (
      multipleUpload &&
      uploadedFiles?.length > 0 &&
      loadEditorView &&
      uploadedFiles?.length === files?.length &&
      uploadedFiles?.every(it => it?.status === 'done')
    ) {
      handleFiles(uploadedFiles)
      setFiles([])
    } else if (!loadEditorView) {
      multipleUpload
        ? handleFiles(uploadedFiles)
        : handleFiles(uploadedFiles[0])
    }
  }, [multipleUpload, files, loadEditorView])

  const removeFileIndex = (uid: string): void => {
    setFiles(files.filter((file, i) => file.uid !== uid))

    if (upload[uid]) {
      upload[uid]?.abort()

      setUpload(previousUpload => {
        return produce(previousUpload, draftState => {
          delete draftState[uid]
        })
      })
    }
  }
  // set file.flag=true if the file has to be rejected by the list
  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    /* regex to check file type */
    const regex = new RegExp(
      `([a-zA-Z0-9s_.-:()])+(${allowedFiles.join('|')})$`
    )

    /* handles case where the multiple upload has files exceeding the limit */
    if (limit && multipleUpload && fileList.length > limit) {
      showMessage({
        type: 'error',
        message: `Maximum ${limit} files are allowed`
      })
      file.flag = true
      return false
    }

    /* check limit of file list if limit is set */
    if (limit && files.length >= limit) {
      setFileLoaderLoading?.(false)
      showMessage({
        type: 'error',
        message: `Maximum ${limit} files are allowed`
      })
      file.flag = true
      return false
    }

    /* text file type */
    if (!regex.test(file.name.toLowerCase()) && category !== 'others') {
      setFileLoaderLoading?.(false)
      showMessage({
        type: 'error',
        message: `Please upload files having extensions: ${allowedFiles.join(
          ', '
        )} only and ensure that the file name does not contain any unsupported characters.`
      })
      file.flag = true
      return false
    }

    /* check file size */
    if (maxFileSize) {
      const isSizeOkay = file.size / 1024 / 1024 < maxFileSize
      if (!isSizeOkay) {
        setFileLoaderLoading?.(false)
        showMessage({
          type: 'error',
          message: `File must be smaller than ${maxFileSize}MB!`
        })
        file.flag = true
        return false
      }
    }

    /* if all checks have passed */
    return true
  }

  const internalOnChange = ({
    file,
    fileList
  }: {
    file: UploadFile
    fileList: UploadFile[]
  }) => {
    const { status, response, flag, error } = file

    onChange(file, fileList)

    // if file.flag is true then the file is rejected by beforeUpload function and update files list in parent component if corresponding function is supplied
    // https://github.com/ant-design/ant-design/issues/2423#issuecomment-233523579
    if (!flag && !['error', 'removed', undefined].includes(status)) {
      if (multipleUpload) {
        const _fileList = fileList
        _fileList.forEach(_file => {
          if (!_file.id) {
            _file.id = _file.response
            _file.base64 = currFileBase64
            _file.thumbUrl =
              ['image/jpeg', 'image/png', 'image/svg+xml'].includes(
                _file.originFileObj?.type ?? ''
              ) && _file.originFileObj
                ? URL.createObjectURL(_file.originFileObj)
                : ''
          }
        })
        // Remove Invalid Files from list
        setFiles(
          fileList.filter(_file => {
            const originFile = _file.originFileObj as RcFile
            return !originFile?.flag && !_file.flag
          })
        )
      } else {
        file.id = response
        file.base64 = currFileBase64
        setFiles([file])
      }
    }

    switch (status) {
      case 'done':
        showMessage({
          type: 'success',
          message: 'File uploaded successfully.'
        })
        break
      case 'removed': {
        const fileId = file.id
        setFiles(files.filter((_file: UploadFile) => fileId !== _file.id))
        break
      }
      case 'error': {
        setFileLoaderLoading?.(false)
        showMessage({ type: 'error', message: error?.message })
        break
      }
      default:
        break
    }
  }

  const handleDownloadFile = async (file: UploadFile) => {
    try {
      if (objectURL) URL.revokeObjectURL(objectURL)

      if (!file.originFileObj && file.id) {
        const fileResponse = await fetchFile({ id: `${file.id}` })
        objectURL = URL.createObjectURL(fileResponse.data)

        const _link = document.createElement('a')
        _link.href = objectURL
        _link.setAttribute('download', file.name)
        _link.click()
        return
      }

      if (!file.originFileObj) return

      const link = document.createElement('a')
      objectURL = URL.createObjectURL(file.originFileObj)

      link.href = objectURL
      link.setAttribute('download', file.name)
      link.click()
    } catch (error) {
      setFileLoaderLoading?.(false)
      showMessage({ type: 'error', message: error.message })
    }
  }

  const handleFilePreview = async (file: UploadFile) => {
    try {
      if (file.url) {
        window.open(file.url, '_blank')
        return
      }

      if (file.thumbUrl) {
        window.open(file.thumbUrl, '_blank')
        return
      }

      if (typeof file.id !== 'string') {
        throw new Error("File doesn't have an id")
      }

      if (objectURL) URL.revokeObjectURL(objectURL)

      const response = await fetchFile({ id: `${file.id}` })

      if (response.status === 'error') {
        throw new Error(response.error)
      }

      objectURL = URL.createObjectURL(response.data)

      window.open(objectURL, '_blank')
    } catch (error) {
      setFileLoaderLoading?.(false)
      showMessage({ type: 'error', message: error.message })
    }
  }

  const props = {
    ...(category !== 'others' && { accept: allowedFiles.join() }),
    multiple: multipleSelection || false,
    beforeUpload,
    disabled: disabled || false,
    showUploadList: false,
    onChange: internalOnChange,
    customRequest: async ({
      onProgress,
      onSuccess,
      onError,
      file
    }: RcCustomRequestOptions): Promise<void> => {
      try {
        setFileLoaderLoading?.(true)
        if (!localStorage.getItem('authToken') && assignmentId) {
          apm.captureError('Unauthorized file upload attempt')
        }
        const fileAssignmentID =
          loadEditorView && assignmentId ? assignmentId : null
        const result = await getUploadUrl(file, fileAssignmentID, isAuthorized)

        if (result.status === 'error') {
          onError(new Error(result.error))
          return
        }

        const _upload = new tus.Upload(file, {
          uploadUrl: result.data.upload_url,
          retryDelays: [0, 1000, 3000, 5000],
          onError: error => {
            console.error(error)
            onError(error)
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percent = +Math.round(
              (bytesUploaded / bytesTotal) * 100
            ).toFixed(2)

            onProgress({ percent }, file)
          },
          onSuccess: () => {
            onSuccess(result.data.id, file)
          }
        })

        // Check if there are any previous uploads to continue.
        _upload.findPreviousUploads().then(previousUploads => {
          // Found previous uploads so we select the first one.
          if (previousUploads.length > 0) {
            _upload.resumeFromPreviousUpload(previousUploads[0])
          }

          // Start the upload
          _upload.start()

          setUpload(_previousUploads => {
            return produce(_previousUploads, draftState => {
              draftState[file.uid] = _upload
            })
          })
        })
      } catch (error) {
        console.error(error)
        onError(error)
        setFileLoaderLoading?.(false)
        showMessage({ type: 'error', message: 'File upload failed.' })
      }
    }
  }

  if (!tus.isSupported) {
    return (
      <Alert
        message="This browser does not support uploads. Please use a modern browser instead."
        type="error"
      />
    )
  }

  if (type === 'minimal') {
    return (
      <div className={className}>
        <Upload className={styles.uploadField} {...props} fileList={files}>
          <Button
            className={styles.uploadMinimalButton}
            size="small"
            type="text"
          >
            <div
              className={clsx({
                [styles.btnTextWrap]: loadV2
              })}
            >
              <Icon icon="ant-design:paper-clip-outlined" />
              {caption}
            </div>
            <span className={styles.suffix}>{suffix}</span>
          </Button>
        </Upload>

        {info && <p className={styles.infoText}>{info}</p>}

        {files.length > 0 && showList && (
          <ul className={`${styles.list} ${styles.minimalList}`}>
            {files.map((file: UploadFile, index: number) => {
              const fileExtension = getFileExtension(file.name)

              return (
                <li key={file.uid}>
                  <Typography.Text ellipsis={true} title={file.name}>
                    {index + 1}.&nbsp;{file.name}
                  </Typography.Text>
                  <span className={styles.iconWrapper}>
                    {file.status === 'uploading' ? (
                      <Icon icon="ant-design:loading-outlined" />
                    ) : (
                      <Fragment>
                        {viewableFileExtensions.includes(fileExtension) && (
                          <Button
                            size="small"
                            type="text"
                            onClick={() => handleFilePreview(file)}
                          >
                            <Icon icon="ant-design:eye-outlined" />
                          </Button>
                        )}
                        <Button
                          size="small"
                          type="text"
                          onClick={() => handleDownloadFile(file)}
                        >
                          <Icon icon="ant-design:download-outlined" />
                        </Button>
                        <Button
                          size="small"
                          type="text"
                          onClick={() => removeFileIndex(file.uid)}
                        >
                          <Icon icon="ant-design:delete-outlined" />
                        </Button>
                      </Fragment>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div
      className={clsx(className, {
        [styles.uploadWrap]: loadV2,
        [styles.editorView]: loadEditorView
      })}
    >
      {/* To se image preview showImagePreview should be true */}
      {showImagePreview && (
        <div className={styles.imageThumbnailContainer}>
          {files.map((file: UploadFile, index) => {
            const fileExtension = getFileExtension(file.name)

            return (
              <div key={file.uid} className={styles.imageThumbnail}>
                <div className={styles.deleteBtn}>
                  {file.status === 'uploading' ? (
                    <Space direction="horizontal" size="middle">
                      <Tooltip title={`${file.percent}%`}>
                        <Progress
                          type="circle"
                          percent={file.percent}
                          showInfo={false}
                          width={18}
                          strokeColor="#2e16e6"
                          strokeWidth={10}
                        />
                      </Tooltip>
                      <Button
                        size="small"
                        type="text"
                        onClick={() => removeFileIndex(file.uid)}
                      >
                        <Icon icon="ant-design:delete-outlined" />
                      </Button>
                    </Space>
                  ) : (
                    <Fragment>
                      {viewableFileExtensions.includes(fileExtension) && (
                        <Button
                          size="small"
                          type="text"
                          onClick={() => handleFilePreview(file)}
                        >
                          <Icon icon="ant-design:eye-outlined" />
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="text"
                        onClick={() => handleDownloadFile(file)}
                      >
                        <Icon icon="ant-design:download-outlined" />
                      </Button>
                      <Button
                        size="small"
                        type="text"
                        onClick={() => removeFileIndex(file.uid)}
                      >
                        <Icon icon="ant-design:delete-outlined" />
                      </Button>
                    </Fragment>
                  )}
                </div>
                {file.thumbUrl ? (
                  <img
                    src={file.thumbUrl}
                    alt={file.name}
                    className={styles.previewImage}
                  />
                ) : (
                  <Icon
                    icon="ant-design:file-image-outlined"
                    className={styles.imageIcon}
                  />
                )}
                <div className={styles.fileName}>
                  <Text showTooltip={true} maxLines={1}>
                    {truncateString(file.name, 25)}
                  </Text>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <Upload
        className={clsx({
          [styles.uploadFieldBtn]: showImagePreview,
          [styles.uploadField]: !showImagePreview
        })}
        {...props}
        fileList={files}
      >
        <Button
          className={clsx(styles.uploadButton, {
            [styles.uploadV2]: loadV2,
            [styles.uploadPepperEditorButton]: loadEditorView
          })}
          block={true}
          disabled={disabled}
        >
          <div
            className={clsx({
              [styles.btnTextWrap]: loadV2
            })}
          >
            <Icon
              icon="ant-design:upload-outlined"
              className={clsx({
                [styles.outlinedGap]: loadV2
              })}
            />
            {caption}
          </div>
          <span className={styles.suffix}>{suffix}</span>
        </Button>
      </Upload>

      {info && <p className={styles.infoText}>{info}</p>}

      {/* custom file list */}
      <ol className={styles.list}>
        {showList &&
          files.map((file: UploadFile, index: number) => {
            const fileExtension = getFileExtension(file.name)

            return (
              <li
                key={file.uid}
                className={clsx(styles.lineWrap, {
                  [styles.error]: !!file.error?.message
                })}
              >
                <Typography.Text
                  ellipsis={true}
                  title={file.name}
                  className={clsx({ [styles.typography]: loadV2 })}
                >
                  {loadV2 && <FileText size={16} className={styles.fileText} />}
                  {!loadV2 && `${index + 1}. `}
                  {file.name}
                  {file.size && (
                    <span
                      className={clsx({
                        [styles.fileWrap]: !loadV2,
                        [styles.listSize]: loadV2
                      })}
                    >
                      {`(${formatBytes(file.size, 0)})`}
                    </span>
                  )}
                </Typography.Text>
                <span className={styles.iconWrapper}>
                  {file.status === 'uploading' ? (
                    <Space direction="horizontal" size="small">
                      <Tooltip title={`${file.percent}%`}>
                        <Progress
                          type="circle"
                          percent={file.percent}
                          showInfo={false}
                          width={18}
                          strokeColor="#2e16e6"
                          strokeWidth={10}
                        />
                      </Tooltip>
                      <Button
                        size="small"
                        type="text"
                        onClick={() => removeFileIndex(file.uid)}
                        disabled={disabled}
                      >
                        <Icon icon="ant-design:close-outlined" />
                      </Button>
                    </Space>
                  ) : (
                    <Fragment>
                      {viewableFileExtensions.includes(fileExtension) && (
                        <Button
                          size="small"
                          type="text"
                          className={styles.highlight}
                          onClick={() => handleFilePreview(file)}
                          disabled={disabled}
                        >
                          <Icon icon="ant-design:eye-outlined" />
                        </Button>
                      )}
                      {file.status !== 'error' && (
                        <Button
                          size="small"
                          type="text"
                          className={styles.highlight}
                          onClick={async () => handleDownloadFile(file)}
                          disabled={disabled}
                        >
                          <Icon icon="ant-design:download-outlined" />
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="text"
                        className={styles.highlight}
                        onClick={() => removeFileIndex(file.uid)}
                        disabled={disabled}
                      >
                        <Icon icon="ant-design:delete-outlined" />
                      </Button>
                    </Fragment>
                  )}
                </span>
              </li>
            )
          })}
      </ol>
    </div>
  )
}
